import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, FileText, RefreshCw, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import { adminApi } from './api';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin-only page listing the three private workshop decks (.pptx).
 *
 * Auth: cookie-based session, same as every other admin page. The list
 * endpoint goes through the shared `adminApi` helper. The binary download
 * uses a raw `fetch(..., { credentials: 'include' })` so the response body
 * stays a Blob (the `api` helper unpacks JSON eagerly).
 *
 * IMPORTANT: the underlying .pptx files live under /app/backend/private_assets
 * \u2014 NOT under /app/frontend/public \u2014 so they are unreachable except via
 * the admin-gated /api/admin/workshop-decks/{id} endpoint.
 */
export default function AdminWorkshops() {
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const loadDecks = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listWorkshopDecks();
      setDecks(data.decks || []);
    } catch (err) {
      logger.error('Failed to load workshop decks', err);
      toast.error('Could not load workshop decks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDecks();
  }, []);

  const downloadDeck = async (deck) => {
    setDownloading(deck.id);
    try {
      const r = await fetch(`${BACKEND}${deck.download_url}`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = deck.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${deck.filename}`);
    } catch (err) {
      logger.error('Workshop deck download failed', err);
      toast.error('Download failed. Make sure you are signed in as admin.');
    } finally {
      setDownloading(null);
    }
  };

  const formatBytes = (n) => {
    if (!n) return '\u2013';
    const kb = n / 1024;
    if (kb < 1024) return `${kb.toFixed(0)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="max-w-[1100px] mx-auto px-5 sm:px-6 py-10" data-testid="admin-workshops">
      <header className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] text-[#004C54] font-bold mb-1">
            Private Sales Collateral
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#0a0d0e]">
            Workshop Decks
          </h1>
          <p className="mt-2 text-[14px] text-foreground/65 max-w-[60ch] leading-relaxed">
            Admin-only downloads of the three live workshop decks. These are{' '}
            <b>not public</b> &mdash; share privately with prospects after a Discovery
            Call, or use as your live presentation source. The public{' '}
            <a href="/workshops" className="text-[#004C54] font-semibold underline underline-offset-2" target="_blank" rel="noreferrer">
              /workshops
            </a>{' '}
            page only shows one sample slide per deck.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadDecks}
          disabled={loading}
          data-testid="admin-workshops-refresh"
          className="shrink-0"
        >
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? (
          [0, 1, 2].map((i) => (
            <Card key={i} className="h-56 animate-pulse bg-foreground/5 border-0" />
          ))
        ) : decks.length === 0 ? (
          <div className="col-span-full text-center py-12 text-foreground/60">
            No decks available. Rerun{' '}
            <code className="text-[12px] bg-foreground/5 px-1.5 py-0.5 rounded">
              python3 backend/scripts/build_workshop_decks.py
            </code>
            .
          </div>
        ) : (
          decks.map((deck) => (
            <Card
              key={deck.id}
              className="border border-[#004C54]/10 hover:border-[#004C54]/30 transition-colors"
              data-testid={`admin-workshop-card-${deck.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-2.5 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#004C54] flex items-center justify-center">
                    <FileText className="h-5 w-5 text-white" aria-hidden="true" />
                  </div>
                  <Lock className="h-3.5 w-3.5 text-[#004C54]/60 ml-auto" aria-hidden="true" />
                </div>
                <h3 className="font-heading text-[1.1rem] font-bold text-[#0a0d0e] leading-tight">
                  {deck.label}
                </h3>
                <div className="mt-2 text-[12.5px] text-foreground/55 font-mono break-all">
                  {deck.filename}
                </div>
                <dl className="mt-4 text-[12.5px] text-foreground/65 space-y-1">
                  <div className="flex justify-between">
                    <dt>Size</dt>
                    <dd className="font-mono">{formatBytes(deck.size_bytes)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt>Status</dt>
                    <dd className={deck.available ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold'}>
                      {deck.available ? 'Ready' : 'Missing'}
                    </dd>
                  </div>
                </dl>
                <Button
                  className="w-full mt-5 bg-[#004C54] hover:bg-[#003a40] text-white font-semibold"
                  onClick={() => downloadDeck(deck)}
                  disabled={!deck.available || downloading === deck.id}
                  data-testid={`admin-workshop-download-${deck.id}`}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {downloading === deck.id ? 'Downloading\u2026' : 'Download .pptx'}
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
